import React, { useCallback, useEffect, useMemo, useState } from 'react';
import syncConnectQueryService from '../services/syncConnectQueryService';
import ConfirmModal from './shared/ConfirmModal';

// THE QUERY LIBRARY — the palette on the right of Admin › Platform › SyncConnect (V585).
//
// Saved OPERA queries, grouped by category, searchable. Picking one opens it as a new query (onUse) for whichever
// project is chosen there — a library query names no hotel; pms_p.resort is the property its prequery initialises.
// Each can be edited or removed (soft delete). The save dialog is exported too: the screen opens it from a new query
// or a sent one, so anything worth asking again is one click from being kept.

const errText = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;

export const LibrarySaveModal = ({ draft, categories, onClose, onSaved }) => {
    const [form, setForm] = useState(draft);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => { setForm(draft); setError(null); }, [draft]);
    if (!draft) return null;

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const body = { title: form.title, category: form.category, description: form.description, sql: form.sql, prequery: form.prequery };
            const res = form.id
                ? await syncConnectQueryService.updateLibrary(form.id, body)
                : await syncConnectQueryService.saveToLibrary(body);
            onSaved(res.data || res);
        } catch (e) {
            setError(errText(e));
            setSaving(false);
        }
    };

    return (
        <div className="odq-modal-overlay" onClick={() => !saving && onClose()}>
            <div className="odq-modal scq-compose" onClick={e => e.stopPropagation()}>
                <div className="odq-modal-head">
                    <span>{form.id ? 'Edit library query' : 'Save to library'}</span>
                    <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={onClose} disabled={saving}>Close</button>
                </div>
                <div className="odq-modal-body scq-form">
                    <label>Title
                        <input value={form.title || ''} placeholder="What it answers — e.g. Card authorisation — does the hotel use it?"
                               onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                    </label>
                    <label>Category
                        <input value={form.category || ''} list="scql-categories" placeholder="Cards & payments, Reservations, Property …"
                               onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
                        <datalist id="scql-categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
                    </label>
                    <label>Description
                        <textarea rows={3} value={form.description || ''} placeholder="When to use it, and how to read the answer"
                                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                    </label>
                    <label>SQL (one SELECT)
                        <textarea className="scq-sql" rows={10} value={form.sql || ''} spellCheck={false}
                                  onChange={e => setForm(f => ({ ...f, sql: e.target.value }))} />
                    </label>
                    <label>Prequery
                        <input className="scq-mono" value={form.prequery || ''} spellCheck={false}
                               onChange={e => setForm(f => ({ ...f, prequery: e.target.value }))} />
                    </label>
                    {error && <div className="os-msg os-msg-error">{error}</div>}
                    <div className="scq-actions">
                        <button className="acp-btn acp-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                        <button className="acp-btn acp-btn-primary" onClick={save}
                                disabled={saving || !String(form.title || '').trim() || !String(form.sql || '').trim()}>
                            {saving ? 'Saving…' : form.id ? 'Save changes' : 'Save to library'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SyncConnectQueryLibrary = ({ refreshKey, onUse, onEdit, onCategories }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [confirmRemove, setConfirmRemove] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await syncConnectQueryService.library();
            const rows = (res.data && res.data.rows) || [];
            setItems(rows);
            if (onCategories) onCategories([...new Set(rows.map(r => r.category).filter(Boolean))].sort());
        } catch (e) {
            setError(errText(e));
        } finally {
            setLoading(false);
        }
    }, [onCategories]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const groups = useMemo(() => {
        const q = search.trim().toLowerCase();
        const shown = q
            ? items.filter(i => [i.title, i.category, i.description, i.sql_text].some(v => String(v || '').toLowerCase().includes(q)))
            : items;
        const by = new Map();
        for (const i of shown) {
            const c = i.category || 'Other';
            if (!by.has(c)) by.set(c, []);
            by.get(c).push(i);
        }
        return [...by.entries()];
    }, [items, search]);

    const remove = async (item) => {
        setConfirmRemove(null);
        try {
            await syncConnectQueryService.removeLibrary(item.id);
            await load();
        } catch (e) {
            setError(errText(e));
        }
    };

    return (
        <aside className="acp-card scql">
            <div className="acp-card-header">
                <span className="acp-card-title">Query library</span>
                <span className="scql-count">{items.length}</span>
            </div>
            <div className="scql-body">
                <input className="scql-search" value={search} placeholder="Search the library…" onChange={e => setSearch(e.target.value)} />
                {error && <div className="os-msg os-msg-error">{error}</div>}
                {loading ? <div className="acp-empty">Loading…</div>
                    : !groups.length ? <div className="acp-empty">{search ? 'Nothing matches.' : 'No saved queries yet — save one from a query.'}</div>
                    : groups.map(([category, list]) => (
                        <div key={category} className="scql-group">
                            <div className="scql-category">{category}</div>
                            {list.map(i => (
                                <div key={i.id} className="scql-item">
                                    <button type="button" className="scql-use" onClick={() => onUse(i)}
                                            title={`${i.description ? `${i.description}\n\n` : ''}${i.sql_text}`}>
                                        <span className="scql-title">{i.title}</span>
                                        {i.description && <span className="scql-desc">{i.description}</span>}
                                    </button>
                                    <div className="scql-tools">
                                        <button type="button" className="scql-tool" onClick={() => onEdit(i)} title="Edit">Edit</button>
                                        <button type="button" className="scql-tool scql-tool-danger" onClick={() => setConfirmRemove(i)} title="Remove">Remove</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
            </div>
            {confirmRemove && (
                <ConfirmModal
                    title="Remove from the library"
                    message={`Remove "${confirmRemove.title}" from the library? Queries already sent with it are not affected.`}
                    confirmLabel="Remove"
                    danger
                    onConfirm={() => remove(confirmRemove)}
                    onCancel={() => setConfirmRemove(null)}
                />
            )}
        </aside>
    );
};

export default SyncConnectQueryLibrary;
